import type { AxiosInstance } from '@aviata/platform-client';
import { flightEntity, accessTokenMemoryStorage } from 'freedom-shared';
import { appLocale } from 'shared';

import { appConfig } from '@/shared/config';

import {
  type AiChatHistoryResponse,
  type PromptExample,
  type StreamEventData,
  type AiChatMessage,
  type AiChatAction,
  type AiChatSessionResponse,
  type AiChatSessionListItem,
  type AiChatSuggestionResponseItem,
  type AiChatHistoryApiMessage,
  type AiChatHistoryApiResponse,
  type AiChatHistoryRichOffersBlock,
  type AiChatHistoryRichTripPlanBlock,
  type AiChatHistoryRichTicketonBlock,
  type TicketonItem,
  type TripPlan,
  type TripPlanDay,
  type StreamSuggestionItem,
} from './types';

export { AiChatActionType } from './types';
export type {
  AiChatHistoryResponse,
  PromptExample,
  StreamEventData,
  AiChatMessage,
  AiChatAction,
  AiChatSessionResponse,
  AiChatSessionListItem,
  AiChatSuggestionResponseItem,
  AiChatHistoryApiMessage,
  AiChatHistoryApiResponse,
  StreamSuggestionItem,
  TicketonItem,
  TripPlan,
  TripPlanDay,
};

const isFetchToolUsing = true;
const isIpAddress = false;
const AI_TRAVEL_CONCIERGE_API_PREFIX = isIpAddress
  ? 'http://46.8.79.73:8043/api/v1/concierge'
  : `${appConfig.pfm.baseUrl}/ai-concierge/api/v1/concierge`;

export const aiChatApi = (http: AxiosInstance) => {
  const getRequestHeaders = (): Record<string, string> => {
    return isIpAddress
      ? {
          'X-Tenant': 'aviata-kz',
          'X-Correlation-ID': '{{uuid}}',
        }
      : {};
  };
  const resolveSessionId = (responseData: AiChatSessionResponse) => {
    return responseData.id || responseData.session_id || '';
  };

  const resolveSuggestions = (
    responseData:
      | PromptExample[]
      | {
          suggestions?: AiChatSuggestionResponseItem[];
          data?: AiChatSuggestionResponseItem[];
        },
  ): PromptExample[] => {
    if (Array.isArray(responseData)) {
      return responseData;
    }

    const suggestions = responseData.suggestions || responseData.data || [];

    return suggestions.map((suggestion, index) => {
      return {
        title: suggestion.title || `Suggestion ${index + 1}`,
        prompt: suggestion.prompt || suggestion.text || '',
        url: suggestion.url || suggestion.image_url || '',
      };
    });
  };

  const mapOfferItemToMappedOffer = (
    offerItem: unknown,
    dict: unknown,
  ): flightEntity.types.MappedOffer => {
    const typedOfferItem = offerItem as {
      id: string;
      flights?: unknown[];
      [key: string]: unknown;
    };

    const mappedFlights = (typedOfferItem.flights || []).map(flight => {
      if (dict) {
        return flightEntity.lib.mapFlight(
          dict as Parameters<typeof flightEntity.lib.mapFlight>[0],
          flight as Parameters<typeof flightEntity.lib.mapFlight>[1],
        );
      }
      return flight;
    });

    return {
      ...typedOfferItem,
      id: typedOfferItem.id,
      flights: mappedFlights,
    } as unknown as flightEntity.types.MappedOffer;
  };

  const resolveMessages = (responseData: unknown): AiChatMessage[] => {
    const mapHistoryMessage = (
      message: AiChatHistoryApiMessage,
    ): AiChatMessage[] => {
      const { content } = message;
      const mappedMessages: AiChatMessage[] = [];
      const pushSuggestionsMessage = (
        rawSuggestions: StreamSuggestionItem[] | undefined,
      ) => {
        const normalizedSuggestions = (rawSuggestions || []).filter(
          suggestion => !!suggestion.text,
        );
        if (normalizedSuggestions.length === 0) {
          return;
        }

        mappedMessages.push({
          sender: message.role || 'assistant',
          content: {
            type: 'suggestions',
            items: normalizedSuggestions,
          },
          created_at: message.created_at || new Date().toISOString(),
        });
      };

      if (content?.type === 'text') {
        mappedMessages.push({
          sender: message.role || 'assistant',
          content: {
            type: 'text',
            text: content.text || '',
          },
          created_at: message.created_at || new Date().toISOString(),
        });
      }

      if (content?.type === 'trip_plan' && content.plan) {
        mappedMessages.push({
          sender: message.role || 'assistant',
          content: {
            type: 'trip_plan',
            plan: content.plan,
          },
          created_at: message.created_at || new Date().toISOString(),
        });
      }

      const contentType = (content as { type?: string } | undefined)?.type;
      const contentSuggestions = (
        content as { suggestions?: StreamSuggestionItem[] } | undefined
      )?.suggestions;
      pushSuggestionsMessage(contentSuggestions);
      if (contentType === 'suggestions') {
        const typedSuggestionsContent = content as {
          items?: StreamSuggestionItem[];
        };
        pushSuggestionsMessage(typedSuggestionsContent.items);
      }

      if (content?.type === 'rich' && Array.isArray(content.blocks)) {
        for (const block of content.blocks) {
          if (block.type === 'text' && block.text) {
            mappedMessages.push({
              sender: message.role || 'assistant',
              content: {
                type: 'text',
                text: block.text || '',
              },
              created_at: message.created_at || new Date().toISOString(),
            });
          }

          if (block.type === 'offers' && block.search_id) {
            const offerBlock = block as AiChatHistoryRichOffersBlock;

            mappedMessages.push({
              sender: message.role || 'assistant',
              content: {
                type: 'offers',
                search_id: block.search_id,
                items: (offerBlock.items || []).map(offerItem =>
                  mapOfferItemToMappedOffer(offerItem, offerBlock.dict),
                ),
              },
              created_at: message.created_at || new Date().toISOString(),
            });
          }

          if (block.type === 'ticketon') {
            const ticketonBlock = block as AiChatHistoryRichTicketonBlock;

            if (
              Array.isArray(ticketonBlock.items) &&
              ticketonBlock.items.length
            ) {
              mappedMessages.push({
                sender: message.role || 'assistant',
                content: {
                  type: 'ticketon',
                  items: ticketonBlock.items,
                },
                created_at: message.created_at || new Date().toISOString(),
              });
            }
          }

          if (block.type === 'trip_plan' && block.plan) {
            const tripPlanBlock = block as AiChatHistoryRichTripPlanBlock;
            mappedMessages.push({
              sender: message.role || 'assistant',
              content: {
                type: 'trip_plan',
                plan: tripPlanBlock.plan as TripPlan,
              },
              created_at: message.created_at || new Date().toISOString(),
            });
          }

          const blockType = (block as { type?: string }).type;
          if (blockType === 'suggestions') {
            const typedSuggestionsBlock = block as {
              items?: StreamSuggestionItem[];
            };
            pushSuggestionsMessage(typedSuggestionsBlock.items);
          }
        }
      }

      return mappedMessages;
    };

    if (Array.isArray(responseData)) {
      return responseData.flatMap(item =>
        mapHistoryMessage(item as AiChatHistoryApiMessage),
      );
    }

    if (responseData && typeof responseData === 'object') {
      const ticketonResponse = responseData as {
        type?: string;
        items?: TicketonItem[];
      };

      if (
        ticketonResponse.type === 'ticketon' &&
        Array.isArray(ticketonResponse.items)
      ) {
        return [
          {
            sender: 'assistant',
            content: {
              type: 'ticketon',
              items: ticketonResponse.items,
            },
            created_at: new Date().toISOString(),
          },
        ];
      }

      const typedResponseData = responseData as AiChatHistoryApiResponse;
      const typedTripPlanResponse = responseData as {
        type?: string;
        plan?: TripPlan;
      };
      if (
        typedTripPlanResponse.type === 'trip_plan' &&
        typedTripPlanResponse.plan
      ) {
        return [
          {
            sender: 'assistant',
            content: {
              type: 'trip_plan',
              plan: typedTripPlanResponse.plan,
            },
            created_at: new Date().toISOString(),
          },
        ];
      }
      const sourceMessages =
        typedResponseData.messages || typedResponseData.data || [];

      return sourceMessages.flatMap(historyMessage =>
        mapHistoryMessage(historyMessage),
      );
    }

    return [];
  };

  return {
    async streamMessage(
      prompt: string,
      onEvent: (eventType: string, data: StreamEventData) => void,
      sessionId: string,
    ) {
      const EVENT_DELIMITER_REGEX = /\r?\n\r?\n/;
      const LINE_DELIMITER_REGEX = /\r?\n/;
      let pendingChunk = '';
      let processedResponseTextLength = 0;

      const handleStreamEventChunk = (streamEventChunk: string) => {
        const normalizedChunk = streamEventChunk.trim();

        if (!normalizedChunk) {
          return;
        }

        const chunkLines = normalizedChunk.split(LINE_DELIMITER_REGEX);
        const eventLine = chunkLines.find(line => line.startsWith('event:'));
        const dataLines = chunkLines
          .filter(line => line.startsWith('data:'))
          .map(line => line.slice('data:'.length).trim());
        const rawData = dataLines.join('\n');

        if (!rawData) {
          return;
        }

        const eventType = eventLine?.slice('event:'.length).trim() || 'message';

        try {
          const parsedData = JSON.parse(rawData) as StreamEventData;

          if (eventType === 'text_chunk') {
            onEvent('text_chunk', {
              message: parsedData.text || parsedData.message || '',
            });
            return;
          }

          if (eventType === 'offers') {
            onEvent('offers', {
              offers: (parsedData.offers || []).map(offer =>
                mapOfferItemToMappedOffer(offer, parsedData.dict),
              ),
            });
            return;
          }

          if (eventType === 'suggestions') {
            onEvent('suggestions', {
              suggestions: parsedData.suggestions || [],
            });
            return;
          }

          if (eventType === 'ticketon') {
            onEvent('ticketon', {
              items: (parsedData.items || []) as TicketonItem[],
            });
            return;
          }

          if (eventType === 'trip_plan') {
            onEvent('trip_plan', parsedData);
            return;
          }

          if (eventType === 'trip_plan_header') {
            onEvent('trip_plan_header', parsedData);
            return;
          }

          if (eventType === 'trip_plan_day') {
            onEvent('trip_plan_day', parsedData);
            return;
          }

          if (eventType === 'trip_plan_done') {
            onEvent('trip_plan_done', parsedData);
            return;
          }

          if (eventType === 'done') {
            onEvent('done', {
              ...parsedData,
              status: 'done',
            });
            return;
          }

          onEvent(eventType, parsedData);
        } catch (error) {
          console.error('Failed to parse SSE data chunk:', rawData, error);
        }
      };

      const processStreamingPayload = (payloadPart: string) => {
        if (!payloadPart) {
          return;
        }

        pendingChunk += payloadPart;
        const streamEvents = pendingChunk.split(EVENT_DELIMITER_REGEX);
        pendingChunk = streamEvents.pop() || '';

        for (const streamEventChunk of streamEvents) {
          handleStreamEventChunk(streamEventChunk);
        }
      };

      const flushPendingStreamEvents = () => {
        const tailStreamEvents = pendingChunk.split(EVENT_DELIMITER_REGEX);
        pendingChunk = '';

        for (const tailStreamEvent of tailStreamEvents) {
          handleStreamEventChunk(tailStreamEvent);
        }
      };

      try {
        if (isFetchToolUsing) {
          const response = await fetch(
            `${AI_TRAVEL_CONCIERGE_API_PREFIX}/sessions/${sessionId}/messages`,
            {
              method: 'POST',
              headers: {
                ...getRequestHeaders(),
                Accept: 'text/event-stream',
                'Content-Type': 'application/json',
                Authorization:
                  `Bearer ${accessTokenMemoryStorage.getAccessToken()}` || '',
              },
              body: JSON.stringify({ text: prompt }),
            },
          );

          if (!response.ok) {
            throw new Error(
              `SSE request failed with status ${response.status}`,
            );
          }

          const reader = response.body?.getReader();
          if (!reader) {
            throw new Error('SSE response body is empty');
          }

          const textDecoder = new TextDecoder();
          const readNextChunk = async (): Promise<void> => {
            const { done, value } = await reader.read();
            if (done) {
              return;
            }

            const payloadPart = textDecoder.decode(value, { stream: true });
            processStreamingPayload(payloadPart);
            await readNextChunk();
          };
          await readNextChunk();
          processStreamingPayload(textDecoder.decode());
        } else {
          await http.post(
            `${AI_TRAVEL_CONCIERGE_API_PREFIX}/sessions/${sessionId}/messages`,
            { text: prompt },
            {
              headers: {
                Accept: 'text/event-stream',
              },
              responseType: 'text',
              onDownloadProgress: progressEvent => {
                const xhr = progressEvent.event?.target as
                  | XMLHttpRequest
                  | undefined;
                const responseText = xhr?.responseText || '';

                if (!responseText) {
                  return;
                }

                const nextPayloadPart = responseText.slice(
                  processedResponseTextLength,
                );
                processedResponseTextLength = responseText.length;

                processStreamingPayload(nextPayloadPart);
              },
            },
          );
        }

        flushPendingStreamEvents();
      } catch (error) {
        console.error('Stream error:', error);
        onEvent('error', { status: 'internal_error' });
      }
    },

    async createSession(): Promise<string> {
      const response = await http.post(
        `${AI_TRAVEL_CONCIERGE_API_PREFIX}/sessions`,
        { locale: appLocale.lib.getAppLocale() },
        {
          headers: getRequestHeaders(),
        },
      );
      return resolveSessionId(response.data as AiChatSessionResponse);
    },

    async getSession(sessionId: string): Promise<AiChatSessionResponse> {
      const response = await http.get(
        `${AI_TRAVEL_CONCIERGE_API_PREFIX}/sessions/${sessionId}`,
        {
          headers: getRequestHeaders(),
        },
      );

      return response.data as AiChatSessionResponse;
    },

    async getSessions(): Promise<AiChatSessionListItem[]> {
      const response = await http.get(
        `${AI_TRAVEL_CONCIERGE_API_PREFIX}/sessions`,
        {
          headers: getRequestHeaders(),
        },
      );

      const responseData = response.data as
        | AiChatSessionListItem[]
        | {
            sessions?: AiChatSessionListItem[];
            data?: AiChatSessionListItem[];
          };

      if (Array.isArray(responseData)) {
        return responseData;
      }

      return responseData.sessions || responseData.data || [];
    },

    async getMessages(sessionId: string): Promise<AiChatHistoryResponse> {
      const response = await http.get(
        `${AI_TRAVEL_CONCIERGE_API_PREFIX}/sessions/${sessionId}/messages`,
        {
          headers: getRequestHeaders(),
        },
      );
      return {
        thread_id: sessionId,
        messages: resolveMessages(response.data),
      };
    },

    async getPromptExamples(sessionId?: string): Promise<PromptExample[]> {
      if (!sessionId) {
        const response = await http.get(
          `${AI_TRAVEL_CONCIERGE_API_PREFIX}/suggestions/initial`,
          {
            params: { locale: appLocale.lib.getAppLocale() },
            headers: getRequestHeaders(),
          },
        );
        return resolveSuggestions(
          response.data as
            | PromptExample[]
            | {
                suggestions?: AiChatSuggestionResponseItem[];
                data?: AiChatSuggestionResponseItem[];
              },
        );
      }

      const response = await http.get(
        `${AI_TRAVEL_CONCIERGE_API_PREFIX}/sessions/${sessionId}/suggestions`,
        {
          headers: getRequestHeaders(),
        },
      );
      return resolveSuggestions(
        response.data as
          | PromptExample[]
          | {
              suggestions?: AiChatSuggestionResponseItem[];
              data?: AiChatSuggestionResponseItem[];
            },
      );
    },
  };
};
